import * as React from 'react'
import moment from 'moment'

import { BusinessTripModel } from '@/app/models/businessTripModel'
import BusinessTripsTable from '../components/tables/BusinessTripsTable'
import { useTable } from '@/modules/drivers/context/TableContext'
import { DateRangeFilter } from '@/app/constants/delegation/dateRangeFilter'
import { DatePickerRangeProps } from '@/modules/drivers/types'
import BusinessTripSingleActionBar from '../components/viewBars/BusinessTripSingleActionBar'
import BusinessTripsSummaryFooter from '../components/BusinessTripsSummaryFooter'
import { RouteComponentProps } from '@/app/navigation/withRouter'

type MatchParams = {
  companyId: string
  driverId?: string
  from?: string
  to?: string
}
interface SingleProps {
  trips: BusinessTripModel[]
  companyId: string
  selectedDriverId: string
  onRefresh: () => void
  dateRangeFilter?: DateRangeFilter
  polishOnlyFilter?: boolean
  onDateRangeFilterChange?: (filter: DateRangeFilter) => void
  onPolishOnlyFilterChange?: (onlyPolish: boolean) => void
  dateRange?: DatePickerRangeProps | null
  match?: RouteComponentProps<MatchParams>['match']
  history?: RouteComponentProps['history']
}

const Single: React.FC<SingleProps> = ({
  trips,
  companyId,
  selectedDriverId,
  onRefresh,
  dateRangeFilter = DateRangeFilter.Started,
  polishOnlyFilter = false,
  onDateRangeFilterChange,
  onPolishOnlyFilterChange,
  dateRange,
  match,
  history,
}) => {
  const [printModalOpen, setPrintModalOpen] = React.useState(false)
  const { selectedGroup, isSelectedAny, resetAll } = useTable()
  const [showAuditDates, setShowAuditDates] = React.useState(true)
  const params = match?.params as any
  const from = params?.from
  const to = params?.to

  const selectedTrips = React.useMemo(() => {
    if (!selectedGroup || !selectedGroup.ids.length) {
      return []
    }
    return trips.filter((trip) => {
      const tripId =
        typeof trip.id === 'string' ? parseInt(trip.id, 10) : trip.id
      return selectedGroup.ids.includes(tripId)
    })
  }, [trips, selectedGroup])

  const sortedTrips = React.useMemo(() => {
    if (!dateRange?.startDate || !dateRange?.endDate) return trips

    const rangeStart = moment({
      year: dateRange.startDate.year,
      month: dateRange.startDate.month - 1,
      date: dateRange.startDate.day,
    }).startOf('day')
    const rangeEnd = moment({
      year: dateRange.endDate.year,
      month: dateRange.endDate.month - 1,
      date: dateRange.endDate.day,
    }).endOf('day')

    if (!rangeStart.isValid() || !rangeEnd.isValid()) return trips

    return trips.filter((trip) => {
      switch (dateRangeFilter) {
        case DateRangeFilter.Started: {
          const d = trip.startDate
          if (!d) return true
          const m = moment.isMoment(d) ? d : moment(d)
          return m.isSameOrAfter(rangeStart) && m.isSameOrBefore(rangeEnd)
        }
        case DateRangeFilter.Ended: {
          const d = trip.endDate
          if (!d) return true
          const m = moment.isMoment(d) ? d : moment(d)
          return m.isSameOrAfter(rangeStart) && m.isSameOrBefore(rangeEnd)
        }

        case DateRangeFilter.All:
        case DateRangeFilter.AllButSettlementMonth:
          return true
        case DateRangeFilter.Modified: {
          const d = trip.modificationDate
          if (!d) return true
          const m = moment.isMoment(d) ? d : moment(d)
          return m.isSameOrAfter(rangeStart) && m.isSameOrBefore(rangeEnd)
        }
        default:
          return true
      }
    })
  }, [trips, dateRangeFilter, dateRange])
  return (
    <div className="tw-flex tw-h-full tw-flex-col tw-bg-white">
     
        <BusinessTripSingleActionBar
          dateRange={dateRange}
          isSelectedAny={isSelectedAny}
          dateRangeFilter={dateRangeFilter}
          polishOnlyFilter={polishOnlyFilter}
          selectedTrips={selectedTrips}
 resetAll={resetAll}
          onDateRangeFilterChange={onDateRangeFilterChange}
          onPolishOnlyFilterChange={onPolishOnlyFilterChange}
          match={match}
          setShowAuditDates={setShowAuditDates}
          showAuditDates={showAuditDates}
          history={history}
          companyId={companyId}
          driverId={selectedDriverId}
          selectedDriverId={selectedDriverId}
          onRefresh={onRefresh}
          from={from}
          to={to}
        />

      {/* Table Container */}
      <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
        <BusinessTripsTable
          showAuditDates={showAuditDates}
          trips={sortedTrips}
          companyId={companyId}
          selectedDriverId={selectedDriverId}
          onRefresh={onRefresh}
        />
      </div>

      <BusinessTripsSummaryFooter trips={sortedTrips} />
    </div>
  )
}

export default Single
